import React, { useState, useEffect } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useSearchParams } from "react-router-dom";
import {
  FiDownload,
  FiUser,
  FiChevronDown,
  FiFileText,
  FiMail,
  FiSearch,
  FiAlertTriangle,
  FiPlus,
  FiX,
} from "react-icons/fi";

const TABS = [
  { id: "mutual-fund", label: "Mutual Fund" },
  { id: "share-bond", label: "Share & Bond" },
  { id: "fixed-deposit", label: "Fixed Deposit" },
  { id: "other-assets", label: "Other Assets" },
];

const EMPTY_TIP =
  "You could try again by selecting a different filter or date range.";

// Shared styles
const styles = {
  primary: "#0d9488",
  primaryLight: "#f0fdfa",
  primaryDark: "#0f766e",
  text: "#1e293b",
  textMuted: "#64748b",
  border: "#e2e8f0",
  bgCard: "#ffffff",
  bgPage: "#f8fafc",
  headerBg: "#f0fdfa",
  tableHeaderBg: "#f8fafc",
  emptyBg: "#fafafa",
};

function TableHeaderCell({ children, align = "left" }) {
  return (
    <th
      style={{
        padding: "14px 16px",
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: styles.textMuted,
        backgroundColor: styles.tableHeaderBg,
        borderBottom: `2px solid ${styles.border}`,
        textAlign: align,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function EmptyState({ tip = EMPTY_TIP }) {
  return (
    <div
      style={{
        padding: "48px 24px",
        textAlign: "center",
        backgroundColor: styles.emptyBg,
        borderRadius: "12px",
        margin: "0 1px 1px 1px",
        border: `1px solid ${styles.border}`,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#fef3c7",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <FiAlertTriangle size={28} style={{ color: "#d97706" }} />
      </div>
      <div style={{ fontSize: "16px", fontWeight: 600, color: styles.text, marginBottom: 6 }}>
        No data available
      </div>
      <div style={{ fontSize: "14px", color: styles.textMuted, maxWidth: 400, margin: "0 auto" }}>
        {tip}
      </div>
    </div>
  );
}

// ----- Modal wrapper (backdrop + box) -----
function ModalBox({ title, onClose, children, width = 520 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: styles.primary,
            color: "#fff",
            fontSize: "18px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <span>{title}</span>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              width: 36,
              height: 36,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
            }}
          >
            <FiX size={20} />
          </button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
}

const modalInput = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  border: `1px solid ${styles.border}`,
  borderRadius: "8px",
  outline: "none",
  boxSizing: "border-box",
};
const modalLabel = { display: "block", marginBottom: 6, fontSize: "14px", fontWeight: 500, color: styles.text };
const modalRow = { display: "flex", gap: 16, marginBottom: 16 };
const modalField = { flex: 1, minWidth: 0 };

export default function MyAssets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "mutual-fund";
  const [activeTab, setActiveTab] = useState(
    TABS.some((t) => t.id === tabFromUrl) ? tabFromUrl : "mutual-fund"
  );

  const [subCategory, setSubCategory] = useState("");
  const [mfDateFrom, setMfDateFrom] = useState("10-01-2026");
  const [mfDateTo, setMfDateTo] = useState("10-02-2026");
  const [searchScrip, setSearchScrip] = useState("");
  const [sbDateFrom, setSbDateFrom] = useState("10-01-2026");
  const [sbDateTo, setSbDateTo] = useState("10-02-2026");
  const [fdStatus, setFdStatus] = useState("running");
  const [oaView, setOaView] = useState("current");

  const [openModal, setOpenModal] = useState(null);

  // Share & Bond form
  const [sbSecuritiesType, setSbSecuritiesType] = useState("listed");
  const [sbInvestor, setSbInvestor] = useState("");
  const [sbTxnDate, setSbTxnDate] = useState("10-02-2026");
  const [sbTxnType, setSbTxnType] = useState("Purchase");
  const [sbShareOrBond, setSbShareOrBond] = useState("share");
  const [sbScrip, setSbScrip] = useState("");
  const [sbPrice, setSbPrice] = useState("");
  const [sbUnit, setSbUnit] = useState("");

  // Fixed Deposit form
  const [fdInvestor, setFdInvestor] = useState("");
  const [fdAllotmentDate, setFdAllotmentDate] = useState("");
  const [fdEntity, setFdEntity] = useState("");
  const [fdIssuer, setFdIssuer] = useState("");
  const [fdPeriodMonth, setFdPeriodMonth] = useState("");
  const [fdPeriodDay, setFdPeriodDay] = useState("");
  const [fdInterestRate, setFdInterestRate] = useState("");
  const [fdMaturityDate, setFdMaturityDate] = useState("");
  const [fdSelectType, setFdSelectType] = useState("");
  const [fdAmount, setFdAmount] = useState("");
  const [fdBank, setFdBank] = useState("");
  const [fdRefNo, setFdRefNo] = useState("");
  const [fdChequeNo, setFdChequeNo] = useState("");
  const [fdRemarks, setFdRemarks] = useState("");

  // Other Asset form
  const [oaInvestor, setOaInvestor] = useState("");
  const [oaSelectType, setOaSelectType] = useState("");
  const [oaDebt, setOaDebt] = useState("");
  const [oaEquity, setOaEquity] = useState("");
  const [oaGlobalEquity, setOaGlobalEquity] = useState("");
  const [oaGold, setOaGold] = useState("");
  const [oaRealEstate, setOaRealEstate] = useState("");
  const [oaOther, setOaOther] = useState("");
  const [oaAssetName, setOaAssetName] = useState("");
  const [oaEntity, setOaEntity] = useState("");
  const [oaPurchaseDate, setOaPurchaseDate] = useState("");
  const [oaPurchaseValue, setOaPurchaseValue] = useState("");
  const [oaCurrentDate, setOaCurrentDate] = useState("");
  const [oaCurrentValue, setOaCurrentValue] = useState("");

  useEffect(() => {
    if (TABS.some((t) => t.id === tabFromUrl)) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const setTab = (id) => {
    setActiveTab(id);
    setSearchParams({ tab: id });
  };

  const handleExportXls = () => alert("Export to Excel will be implemented");
  const handleExportPdf = () => alert("Export to PDF will be implemented");
  const handleShare = () => alert("Share will be implemented");
  const openShareBondModal = () => setOpenModal("share-bond");
  const openFixedDepositModal = () => setOpenModal("fixed-deposit");
  const openOtherAssetModal = () => setOpenModal("other-assets");
  const closeModal = () => setOpenModal(null);

  const inputBase = {
    padding: "8px 12px",
    fontSize: "14px",
    border: `1px solid ${styles.border}`,
    borderRadius: "8px",
    outline: "none",
    minHeight: 40,
  };
  const btnPrimary = {
    backgroundColor: styles.primary,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
  };
  const btnSecondary = {
    backgroundColor: "#fff",
    color: styles.primary,
    border: `1px solid ${styles.primary}`,
    borderRadius: "8px",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  };

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: styles.bgPage, minHeight: "100vh" }}>
        {/* Page header */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div
            className="card-body p-4"

          >
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h3 className="text-primary mb-2">My Assets Investment </h3>
                <nav
                  aria-label="breadcrumb"
                  style={{ "--bs-breadcrumb-divider": "none" }}
                >
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <NavLink to="/overview">Dashboard</NavLink> /
                    </li>
                    <li className="breadcrumb-item active">My Assets</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div
          style={{
            background: styles.bgCard,
            borderRadius: "12px",
            border: `1px solid ${styles.border}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Filter bar */}
          <div
            style={{
              padding: "20px 24px",
              borderBottom: `1px solid ${styles.border}`,
              backgroundColor: "#fafafa",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16,
            }}
          >
            {activeTab === "mutual-fund" && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label style={{ fontSize: "13px", color: styles.textMuted, fontWeight: 500 }}>Sub Category</label>
                  <select
                    value={subCategory}
                    onChange={(e) => setSubCategory(e.target.value)}
                    style={{ ...inputBase, width: 160 }}
                  >
                    <option value="">Select</option>
                    <option value="equity">Equity</option>
                    <option value="debt">Debt</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="text" value={mfDateFrom} onChange={(e) => setMfDateFrom(e.target.value)} placeholder="From" style={{ ...inputBase, width: 130 }} />
                  <span style={{ color: styles.textMuted, fontSize: "13px" }}>to</span>
                  <input type="text" value={mfDateTo} onChange={(e) => setMfDateTo(e.target.value)} placeholder="To" style={{ ...inputBase, width: 130 }} />
                </div>
              </>
            )}
            {activeTab === "share-bond" && (
              <>
                <div style={{ position: "relative", width: 220 }}>
                  <FiSearch size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: styles.textMuted }} />
                  <input
                    type="text"
                    value={searchScrip}
                    onChange={(e) => setSearchScrip(e.target.value)}
                    placeholder="Search Scrip"
                    style={{ ...inputBase, width: "100%", paddingLeft: 36 }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="text" value={sbDateFrom} onChange={(e) => setSbDateFrom(e.target.value)} placeholder="From" style={{ ...inputBase, width: 130 }} />
                  <span style={{ color: styles.textMuted, fontSize: "13px" }}>to</span>
                  <input type="text" value={sbDateTo} onChange={(e) => setSbDateTo(e.target.value)} placeholder="To" style={{ ...inputBase, width: 130 }} />
                </div>
              </>
            )}
            {activeTab === "fixed-deposit" && (
              <div style={{ display: "flex", backgroundColor: styles.border, borderRadius: "8px", padding: 4, gap: 4 }}>
                {["running", "matured"].map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFdStatus(key)}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      textTransform: "capitalize",
                      backgroundColor: fdStatus === key ? styles.primary : "transparent",
                      color: fdStatus === key ? "#fff" : styles.textMuted,
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            )}
            {activeTab === "other-assets" && (
              <div style={{ display: "flex", backgroundColor: styles.border, borderRadius: "8px", padding: 4, gap: 4 }}>
                {[
                  { key: "current", label: "Current value" },
                  { key: "transaction", label: "Transaction" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setOaView(key)}
                    style={{
                      padding: "8px 20px",
                      borderRadius: "6px",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      backgroundColor: oaView === key ? styles.primary : "transparent",
                      color: oaView === key ? "#fff" : styles.textMuted,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <button type="button" style={btnSecondary} onClick={() => { }}>
                <FiUser size={18} /> Select User <FiChevronDown size={14} />
              </button>
              {(activeTab === "share-bond" || activeTab === "fixed-deposit") && (
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={activeTab === "share-bond" ? openShareBondModal : openFixedDepositModal}
                >
                  <FiPlus size={18} /> Add Transaction
                </button>
              )}
              {activeTab === "other-assets" && (
                <button type="button" style={btnPrimary} onClick={openOtherAssetModal}>
                  <FiPlus size={18} /> Create Asset
                </button>
              )}
              {activeTab === "mutual-fund" && (
                <>
                  <button type="button" style={btnSecondary} onClick={handleExportXls}><FiFileText size={16} /> XLS</button>
                  <button type="button" style={btnSecondary} onClick={handleExportPdf}><FiDownload size={16} /> PDF</button>
                  <button type="button" style={btnSecondary} onClick={handleShare}><FiMail size={16} /></button>
                </>
              )}
              {(activeTab === "share-bond" || activeTab === "fixed-deposit" || activeTab === "other-assets") && (
                <button type="button" style={btnSecondary} onClick={handleExportXls}><FiFileText size={16} /> XLS</button>
              )}
            </div>
          </div>

          {/* Tabs - only one active */}
          <div style={{ display: "flex", borderBottom: `1px solid ${styles.border}`, paddingLeft: 24, gap: 0 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setTab(tab.id)}
                style={{
                  padding: "14px 20px 14px 20px",
                  border: "none",
                  borderBottom: activeTab === tab.id ? `3px solid ${styles.primary}` : "3px solid transparent",
                  background: "none",
                  fontSize: "14px",
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  color: activeTab === tab.id ? styles.primary : styles.textMuted,
                  cursor: "pointer",
                  marginBottom: -1,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="table-responsive">
            {activeTab === "mutual-fund" && (
              <table className="table mb-0" style={{ fontSize: "14px" }}>
                <thead>
                  <tr>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Scheme Name</TableHeaderCell>
                    <TableHeaderCell>Folio</TableHeaderCell>
                    <TableHeaderCell>Applicant</TableHeaderCell>
                    <TableHeaderCell>Transaction Type</TableHeaderCell>
                    <TableHeaderCell align="right">Units</TableHeaderCell>
                    <TableHeaderCell align="right">Price (₹)</TableHeaderCell>
                    <TableHeaderCell align="right">Amount (₹)</TableHeaderCell>
                    <TableHeaderCell align="right">Stamp Duty</TableHeaderCell>
                    <TableHeaderCell align="right">TDS</TableHeaderCell>
                    <TableHeaderCell align="right">STT</TableHeaderCell>
                    <TableHeaderCell align="right">Total</TableHeaderCell>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan={12} className="p-0 border-0"><EmptyState /></td></tr>
                </tbody>
              </table>
            )}
            {activeTab === "share-bond" && (
              <table className="table mb-0" style={{ fontSize: "14px" }}>
                <thead>
                  <tr>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Investor Name</TableHeaderCell>
                    <TableHeaderCell>Scrip Name</TableHeaderCell>
                    <TableHeaderCell>Transaction Type</TableHeaderCell>
                    <TableHeaderCell align="right">Unit</TableHeaderCell>
                    <TableHeaderCell align="right">Price (₹)</TableHeaderCell>
                    <TableHeaderCell align="right">Amount (₹)</TableHeaderCell>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan={7} className="p-0 border-0"><EmptyState /></td></tr>
                </tbody>
              </table>
            )}
            {activeTab === "fixed-deposit" && (
              <table className="table mb-0" style={{ fontSize: "14px" }}>
                <thead>
                  <tr>
                    <TableHeaderCell>Allotment Date</TableHeaderCell>
                    <TableHeaderCell>Investor Name</TableHeaderCell>
                    <TableHeaderCell>Scheme</TableHeaderCell>
                    <TableHeaderCell>Period</TableHeaderCell>
                    <TableHeaderCell>Maturity Date</TableHeaderCell>
                    <TableHeaderCell align="right">Amount</TableHeaderCell>
                    <TableHeaderCell>Actions</TableHeaderCell>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan={7} className="p-0 border-0"><EmptyState /></td></tr>
                </tbody>
              </table>
            )}
            {activeTab === "other-assets" && (
              <table className="table mb-0" style={{ fontSize: "14px" }}>
                <thead>
                  <tr>
                    <TableHeaderCell>Investor Name</TableHeaderCell>
                    <TableHeaderCell>Asset Type</TableHeaderCell>
                    <TableHeaderCell align="right">Debt (%)</TableHeaderCell>
                    <TableHeaderCell align="right">Equity (%)</TableHeaderCell>
                    <TableHeaderCell align="right">Global Equity (%)</TableHeaderCell>
                    <TableHeaderCell align="right">Gold (%)</TableHeaderCell>
                    <TableHeaderCell align="right">Real Estate (%)</TableHeaderCell>
                    <TableHeaderCell align="right">Other (%)</TableHeaderCell>
                    <TableHeaderCell>Asset Name</TableHeaderCell>
                    <TableHeaderCell>Account no.</TableHeaderCell>
                    <TableHeaderCell>Current Date</TableHeaderCell>
                    <TableHeaderCell align="right">Current Value (₹)</TableHeaderCell>
                  </tr>
                </thead>
                <tbody>
                  <tr><td colSpan={12} className="p-0 border-0"><EmptyState /></td></tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ----- POPUP 1: Share & Bond Transaction ----- */}
      {openModal === "share-bond" && (
        <ModalBox title="Share & Bond Transaction" onClose={closeModal} width={520}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="sbSecurities"
                  checked={sbSecuritiesType === "listed"}
                  onChange={() => setSbSecuritiesType("listed")}
                  style={{ accentColor: styles.primary }}
                />
                <span style={{ fontSize: "14px" }}>Listed Securities</span>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="radio"
                  name="sbSecurities"
                  checked={sbSecuritiesType === "user-defined"}
                  onChange={() => setSbSecuritiesType("user-defined")}
                  style={{ accentColor: styles.primary }}
                />
                <span style={{ fontSize: "14px" }}>User Defined Securities</span>
              </label>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Investor:</label>
              <select value={sbInvestor} onChange={(e) => setSbInvestor(e.target.value)} style={modalInput}>
                <option value="">Select</option>
                <option value="1">Investor 1</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Transaction Date :</label>
              <input type="text" value={sbTxnDate} onChange={(e) => setSbTxnDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Transaction Type:</label>
              <select value={sbTxnType} onChange={(e) => setSbTxnType(e.target.value)} style={modalInput}>
                <option value="Purchase">Purchase</option>
                <option value="Sale">Sale</option>
              </select>
            </div>
          </div>
          <div style={{ ...modalRow, alignItems: "center" }}>
            <span style={{ fontSize: "14px", fontWeight: 500 }}>Share</span>
            <div
              style={{
                width: 52,
                height: 28,
                borderRadius: 14,
                background: sbShareOrBond === "share" ? styles.primary : styles.border,
                position: "relative",
                cursor: "pointer",
              }}
              onClick={() => setSbShareOrBond(sbShareOrBond === "share" ? "bond" : "share")}
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: "#fff",
                  position: "absolute",
                  top: 2,
                  left: sbShareOrBond === "share" ? 2 : 26,
                  transition: "left 0.2s",
                }}
              />
            </div>
            <span style={{ fontSize: "14px", fontWeight: 500 }}>Bond</span>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Scrip:</label>
              <select value={sbScrip} onChange={(e) => setSbScrip(e.target.value)} style={modalInput}>
                <option value="">Scrip</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Price</label>
              <input type="text" value={sbPrice} onChange={(e) => setSbPrice(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Unit</label>
              <input type="text" value={sbUnit} onChange={(e) => setSbUnit(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button type="button" style={btnPrimary} onClick={() => { alert("Submit Share & Bond"); closeModal(); }}>
              Submit
            </button>
          </div>
        </ModalBox>
      )}

      {/* ----- POPUP 2: Fixed Deposit Entry Form ----- */}
      {openModal === "fixed-deposit" && (
        <ModalBox title="Fixed Deposit Entry Form" onClose={closeModal} width={560}>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Investor:</label>
              <select value={fdInvestor} onChange={(e) => setFdInvestor(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Allotment Date:</label>
              <input type="text" value={fdAllotmentDate} onChange={(e) => setFdAllotmentDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Entity:</label>
              <select value={fdEntity} onChange={(e) => setFdEntity(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>FD Issuer:</label>
              <input type="text" value={fdIssuer} onChange={(e) => setFdIssuer(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={{ flex: 1, minWidth: 0, display: "flex", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={modalLabel}>Period:</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input type="text" value={fdPeriodMonth} onChange={(e) => setFdPeriodMonth(e.target.value)} placeholder="Month" style={{ ...modalInput, flex: 1 }} />
                  <input type="text" value={fdPeriodDay} onChange={(e) => setFdPeriodDay(e.target.value)} placeholder="Day" style={{ ...modalInput, flex: 1 }} />
                </div>
              </div>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Interest Rate (%):</label>
              <input type="text" value={fdInterestRate} onChange={(e) => setFdInterestRate(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Maturity Date:</label>
              <input type="text" value={fdMaturityDate} onChange={(e) => setFdMaturityDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Type:</label>
              <select value={fdSelectType} onChange={(e) => setFdSelectType(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Amount:</label>
              <input type="text" value={fdAmount} onChange={(e) => setFdAmount(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Bank:</label>
              <input type="text" value={fdBank} onChange={(e) => setFdBank(e.target.value)} placeholder="Enter Bank Name" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Ref No:</label>
              <input type="text" value={fdRefNo} onChange={(e) => setFdRefNo(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Cheque no. OR FDR no:</label>
              <input type="text" value={fdChequeNo} onChange={(e) => setFdChequeNo(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Remarks:</label>
              <input type="text" value={fdRemarks} onChange={(e) => setFdRemarks(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button type="button" style={btnPrimary} onClick={() => { alert("Submit Fixed Deposit"); closeModal(); }}>
              Submit
            </button>
          </div>
        </ModalBox>
      )}

      {/* ----- POPUP 3: Create Other Asset ----- */}
      {openModal === "other-assets" && (
        <ModalBox title="Create Other Asset" onClose={closeModal} width={520}>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Investor:</label>
              <select value={oaInvestor} onChange={(e) => setOaInvestor(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Select Type :</label>
              <select value={oaSelectType} onChange={(e) => setOaSelectType(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ ...modalLabel, marginBottom: 10 }}>Composition :</label>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Debt (%):</label>
                <input type="text" value={oaDebt} onChange={(e) => setOaDebt(e.target.value)} style={modalInput} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Equity (%):</label>
                <input type="text" value={oaEquity} onChange={(e) => setOaEquity(e.target.value)} style={modalInput} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Global Equity (%):</label>
                <input type="text" value={oaGlobalEquity} onChange={(e) => setOaGlobalEquity(e.target.value)} style={modalInput} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Gold (%):</label>
                <input type="text" value={oaGold} onChange={(e) => setOaGold(e.target.value)} style={modalInput} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Real Estate (%):</label>
                <input type="text" value={oaRealEstate} onChange={(e) => setOaRealEstate(e.target.value)} style={modalInput} />
              </div>
              <div style={{ flex: "1 1 140px" }}>
                <label style={{ ...modalLabel, fontSize: "13px" }}>Other (%):</label>
                <input type="text" value={oaOther} onChange={(e) => setOaOther(e.target.value)} style={modalInput} />
              </div>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Asset Name</label>
              <input type="text" value={oaAssetName} onChange={(e) => setOaAssetName(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Entity:</label>
              <select value={oaEntity} onChange={(e) => setOaEntity(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Purchase Date:</label>
              <input type="text" value={oaPurchaseDate} onChange={(e) => setOaPurchaseDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Purchase Value</label>
              <input type="text" value={oaPurchaseValue} onChange={(e) => setOaPurchaseValue(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Current Date:</label>
              <input type="text" value={oaCurrentDate} onChange={(e) => setOaCurrentDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Current Value</label>
              <input type="text" value={oaCurrentValue} onChange={(e) => setOaCurrentValue(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button type="button" style={btnPrimary} onClick={() => { alert("Submit Create Other Asset"); closeModal(); }}>
              Submit
            </button>
          </div>
        </ModalBox>
      )}
    </>
  );
}
